import { useEffect, useState } from 'react';
import { getMisPolizas, updateTelefonoPoliza, getAgenteResumen } from '../services/api';
import WhatsAppModal from '../components/WhatsAppModal';
import SeguimientoModal from '../components/SeguimientoModal';

type Tab = 'pendiente' | 'anulada' | 'vigor' | 'todas';

interface Poliza {
  id: number;
  numeroPoliza: string;
  contratante: string;
  asegurado: string;
  producto: string;
  estatus: string;
  pendientePago: boolean;
  fechaVencimiento: string;
  fechaLimitePago: string;
  fechaEmision: string;
  sumaAsegurada: string;
  formaPago: string;
  telefonoParticular: string;
  telefonoOverride: string | null;
  telefono: string;
  ciudad: string;
  estado: string;
  actualizadoEn: string;
}

const TAB_CONFIG: { id: Tab; label: string; emptyMsg: string; rowClass: (p: Poliza) => string }[] = [
  {
    id: 'pendiente',
    label: '⚠️ Pendientes de pago',
    emptyMsg: 'No hay pólizas con recibos pendientes',
    rowClass: () => 'bg-orange-50/40',
  },
  {
    id: 'anulada',
    label: '🔴 Anuladas',
    emptyMsg: 'No hay pólizas anuladas',
    rowClass: () => 'bg-red-50/30',
  },
  {
    id: 'vigor',
    label: '🟡 Vigor Prorrogado',
    emptyMsg: 'No hay pólizas en Vigor Prorrogado',
    rowClass: () => 'bg-yellow-50/30',
  },
  {
    id: 'todas',
    label: 'Todas mis pólizas',
    emptyMsg: 'No hay pólizas',
    rowClass: (p) => p.pendientePago ? 'bg-orange-50/40' : '',
  },
];

export default function MisPolizas() {
  const [tab, setTab] = useState<Tab>('pendiente');
  const [polizas, setPolizas] = useState<Poliza[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingPhone, setEditingPhone] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [resumen, setResumen] = useState<{ totalPolizas: number; totalPendientes: number; ultimaActualizacion: string | null } | null>(null);
  const [showWaModal, setShowWaModal] = useState(false);
  const [seguimientoPoliza, setSeguimientoPoliza] = useState<{ id: number; numeroPoliza: string; contratante: string } | null>(null);
  const [seguimientoIds, setSeguimientoIds] = useState<Set<number>>(new Set());

  const load = async (activeTab: Tab) => {
    setLoading(true);
    setSelected(new Set());
    try {
      const res = await getMisPolizas(activeTab);
      setPolizas(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(tab);
    getAgenteResumen().then(r => setResumen(r.data)).catch(() => {});
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = polizas.filter(p => {
    if (!search) return true;
    const s = search.toLowerCase();
    return p.contratante.toLowerCase().includes(s) ||
           p.numeroPoliza.toLowerCase().includes(s) ||
           p.producto.toLowerCase().includes(s);
  });

  const toggleSelect = (id: number) =>
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const selectAll = () => setSelected(new Set(filtered.map(p => p.id)));
  const clearSelection = () => setSelected(new Set());

  const startEditPhone = (p: Poliza) => {
    setEditingId(p.id);
    setEditingPhone(p.telefonoOverride ?? p.telefonoParticular ?? '');
    setPhoneError(null);
  };

  const savePhone = async (id: number) => {
    setPhoneError(null);
    try {
      await updateTelefonoPoliza(id, editingPhone);
      setPolizas(prev => prev.map(p =>
        p.id === id ? { ...p, telefonoOverride: editingPhone || null, telefono: editingPhone || p.telefonoParticular } : p
      ));
      setEditingId(null);
    } catch (err: any) {
      setPhoneError(err.response?.data?.message || 'Error al guardar teléfono');
    }
  };

  const selectedPolizas = filtered.filter(p => selected.has(p.id));
  const waDestinatarios = selectedPolizas.map(p => ({
    id: p.id,
    contratante: p.contratante,
    telefono: p.telefono,
    numeroPoliza: p.numeroPoliza,
    producto: p.producto,
    fechaVencimiento: p.fechaVencimiento,
    fechaLimitePago: p.fechaLimitePago,
  }));

  const closeSeguimiento = () => setSeguimientoPoliza(null);
  const onSeguimientoSaved = (polizaId: number, enSeguimiento: boolean) => {
    setSeguimientoIds(prev => {
      const s = new Set(prev);
      enSeguimiento ? s.add(polizaId) : s.delete(polizaId);
      return s;
    });
  };

  const formatDate = (raw: string) => raw || '—';
  const activeConfig = TAB_CONFIG.find(t => t.id === tab)!;

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Mis Pólizas</h2>
          {resumen && (
            <p className="text-sm text-gray-500 mt-0.5">
              Total: {resumen.totalPolizas} &nbsp;·&nbsp;
              Pendientes: <span className="text-orange-600 font-medium">{resumen.totalPendientes}</span>
              {resumen.ultimaActualizacion && (
                <> &nbsp;·&nbsp; Última actualización: {new Date(resumen.ultimaActualizacion).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</>
              )}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2">
          {selected.size > 0 ? (
            <>
              <span className="text-sm text-gray-600">{selected.size} seleccionadas</span>
              <button onClick={clearSelection}
                className="text-xs text-gray-500 hover:text-gray-700 underline">Limpiar</button>
              <button
                onClick={() => setShowWaModal(true)}
                className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-700"
              >
                <span>💬</span> Enviar WhatsApp ({selected.size})
              </button>
            </>
          ) : filtered.length > 0 ? (
            <span className="text-xs text-gray-400">
              Marca el checkbox de una o varias pólizas para enviar WhatsApp
            </span>
          ) : null}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 mb-4 bg-gray-100 rounded-xl p-1 w-fit">
        {TAB_CONFIG.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
              tab === t.id ? 'bg-white shadow-sm text-primary-700' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input
          type="text"
          placeholder="Buscar por nombre, póliza o producto..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-72"
        />
        {filtered.length > 0 && (
          <button onClick={selectAll}
            className="text-sm text-primary-600 hover:text-primary-800 underline">
            Seleccionar todos ({filtered.length})
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
            <tr>
              <th className="px-3 py-3 text-left w-8">
                <input type="checkbox"
                  checked={filtered.length > 0 && filtered.every(p => selected.has(p.id))}
                  onChange={e => e.target.checked ? selectAll() : clearSelection()}
                  className="h-4 w-4" />
              </th>
              <th className="px-3 py-3 text-left">Póliza</th>
              <th className="px-3 py-3 text-left">Contratante</th>
              <th className="px-3 py-3 text-left">Producto</th>
              <th className="px-3 py-3 text-left">Estatus</th>
              <th className="px-3 py-3 text-left">Emisión</th>
              <th className="px-3 py-3 text-left">Vencimiento</th>
              <th className="px-3 py-3 text-left">Límite pago</th>
              <th className="px-3 py-3 text-left">Teléfono</th>
              <th className="px-3 py-3 text-left">Seguimiento</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan={10} className="px-4 py-10 text-center text-gray-400">
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                    Cargando...
                  </div>
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-4 py-10 text-center text-gray-400">
                  {activeConfig.emptyMsg}
                </td>
              </tr>
            ) : filtered.map(p => (
              <tr key={p.id}
                className={`hover:bg-gray-50 ${activeConfig.rowClass(p)} ${selected.has(p.id) ? 'bg-primary-50/60' : ''}`}
              >
                <td className="px-3 py-3">
                  <input type="checkbox"
                    checked={selected.has(p.id)}
                    onChange={() => toggleSelect(p.id)}
                    className="h-4 w-4" />
                </td>
                <td className="px-3 py-3 font-mono text-xs text-gray-700">{p.numeroPoliza}</td>
                <td className="px-3 py-3">
                  <div className="font-medium text-gray-800">{p.contratante}</div>
                  {p.asegurado && p.asegurado !== p.contratante && (
                    <div className="text-xs text-gray-400">{p.asegurado}</div>
                  )}
                </td>
                <td className="px-3 py-3 text-gray-600">{p.producto}</td>
                <td className="px-3 py-3">
                  {p.pendientePago ? (
                    <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-medium">⚠️ Pendiente</span>
                  ) : (
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{p.estatus}</span>
                  )}
                </td>
                <td className="px-3 py-3 text-xs text-gray-600">{formatDate(p.fechaEmision)}</td>
                <td className="px-3 py-3 text-xs text-gray-600">{formatDate(p.fechaVencimiento)}</td>
                <td className="px-3 py-3 text-xs text-gray-600">{formatDate(p.fechaLimitePago)}</td>
                <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                  {editingId === p.id ? (
                    <div className="flex flex-col gap-0.5">
                      <div className="flex items-center gap-1">
                        <input
                          type="text"
                          value={editingPhone}
                          onChange={e => setEditingPhone(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') savePhone(p.id); if (e.key === 'Escape') setEditingId(null); }}
                          autoFocus
                          className="border border-primary-400 rounded px-2 py-0.5 text-xs w-32"
                        />
                        <button onClick={() => savePhone(p.id)}
                          className="text-green-600 hover:text-green-800 text-sm">✓</button>
                        <button onClick={() => { setEditingId(null); setPhoneError(null); }}
                          className="text-gray-400 hover:text-gray-600 text-sm">✕</button>
                      </div>
                      {phoneError && p.id === editingId && (
                        <span className="text-red-500 text-xs">{phoneError}</span>
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={() => startEditPhone(p)}
                      className={`text-xs px-2 py-0.5 rounded hover:bg-gray-100 text-left ${p.telefonoOverride ? 'text-primary-700 font-medium' : 'text-gray-600'}`}
                      title={p.telefonoOverride ? `Editado (original: ${p.telefonoParticular})` : 'Click para editar'}
                    >
                      {p.telefono || <span className="text-gray-300">—</span>}
                      {p.telefonoOverride && <span className="ml-1 text-primary-400">✏️</span>}
                    </button>
                  )}
                </td>
                <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                  <button
                    onClick={() => setSeguimientoPoliza({ id: p.id, numeroPoliza: p.numeroPoliza, contratante: p.contratante })}
                    className="text-xs px-2 py-0.5 rounded hover:bg-gray-100"
                    title="Ver seguimiento"
                  >
                    {seguimientoIds.has(p.id)
                      ? <span className="text-green-600 font-medium">✓ En seguimiento</span>
                      : <span className="text-gray-400">— Agregar</span>}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filtered.length > 0 && (
        <p className="text-xs text-gray-400 mt-2 px-1">{filtered.length} pólizas</p>
      )}

      {showWaModal && (
        <WhatsAppModal
          destinatarios={waDestinatarios}
          onClose={() => setShowWaModal(false)}
        />
      )}

      {seguimientoPoliza && (
        <SeguimientoModal
          polizaId={seguimientoPoliza.id}
          numeroPoliza={seguimientoPoliza.numeroPoliza}
          contratante={seguimientoPoliza.contratante}
          onClose={closeSeguimiento}
          onSaved={en => onSeguimientoSaved(seguimientoPoliza.id, en)}
        />
      )}
    </div>
  );
}
